import { schemas, useClient, useMutation } from '@weenat/client'
import { useIntl } from '@weenat/wintl'
import Text from 'app/src/kit/Text'
import { useUserContext } from 'app/userProvider'
import { FormikProps, useFormikContext } from 'formik'
import { isNil } from 'lodash-es'
import { useRef, useState } from 'react'
import useDisclosure from '../hooks/useDisclosure'
import useToasts from '../hooks/useToasts'
import Badge from '../kit/Badge'
import Button, { ButtonProps } from '../kit/Button'
import ConfirmationModal from '../kit/ConfirmationModal'
import EmailField from '../kit/fields/EmailField'
import Icons from '../kit/Icons'
import { ModalProps } from '../kit/Modal'
import SuperForm from '../kit/SuperForm'
import AccountSection from './AccountSection'

const VerifiedBadge = ({ isVerified }: { isVerified: boolean }) => {
  const { t } = useIntl()
  return isVerified ? (
    <Badge
      $backgroundColor={'feedback.success.200'}
      $gap='sm'
      $borderRadius={'sm'}
      $px='sm'
      $py='xs'
    >
      <Icons.CheckCircle $size='lg' $color={'feedback.success.500'} />
      <Text $fontWeight='semiBold' $color={'feedback.success.500'}>
        {t('auth.email_verified_adjective', { capitalize: true })}
      </Text>
    </Badge>
  ) : (
    <Badge $backgroundColor={'feedback.warning.200'} $gap='sm' $borderRadius={'sm'} $pl='sm'>
      <Icons.AlertTriangle $size='lg' $color={'feedback.warning.500'} />
      <Text $fontWeight='semiBold' $color={'feedback.warning.500'}>
        {t('auth.email_not_verified_adjective', { capitalize: true })}
      </Text>
    </Badge>
  )
}

const SCHEMA = schemas.shared.mailed
type FormValues = typeof SCHEMA.initialValues

interface FormAwareConfirmationModalProps extends ModalProps {
  sendVerificationEmailRequest: ButtonProps['state']
}

const FormAwareConfirmationModal: FC<FormAwareConfirmationModalProps> = ({
  isOpen,
  close,
  sendVerificationEmailRequest
}) => {
  const { values, submitForm } = useFormikContext<FormValues>()
  const { t } = useIntl()

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onCancel={close}
      onConfirm={() => {
        submitForm()
      }}
      confirmButtonState={sendVerificationEmailRequest}
      confirmationMessage={t('auth.verify_email_dialog_message', { email: values.email })}
      title={t('auth.verify_email')}
    />
  )
}

const ConfirmButton: FC<{
  onPress: () => void
}> = ({ onPress }) => {
  const { t } = useIntl()
  const { isValid } = useFormikContext<FormValues>()
  return (
    <Button variant='compact' isDisabled={!isValid} onPress={onPress}>
      {t('actions.confirm')}
    </Button>
  )
}

const CancelButton: FC<{
  onPress: () => void
}> = ({ onPress }) => {
  const { t } = useIntl()
  const { resetForm } = useFormikContext<FormValues>()
  return (
    <Button
      importance='sd'
      variant='compact'
      onPress={() => {
        onPress()
        resetForm()
      }}
    >
      {t('actions.cancel')}
    </Button>
  )
}

const ChangeEmail = () => {
  const { t } = useIntl()
  const { me } = useUserContext()
  const client = useClient()
  const { addSuccessToast, addErrorToast } = useToasts()

  const confirmationDialog = useDisclosure()
  const formRef = useRef<FormikProps<FormValues>>(null)

  const [allowEmailEdition, setAllowEmailEdition] = useState(false)

  const [changeEmail, changeEmailRequest] = useMutation(client.auth.changeEmail(), {
    onSuccess: () => {
      const receiver = formRef.current?.values.email
      addSuccessToast(t('auth.verify_email_sent', { email: receiver ?? '' }))

      confirmationDialog.close()
      formRef.current?.resetForm()
      setAllowEmailEdition(false)
    },
    onError: (exception) => {
      const error = (exception.parsedErrors?.['new_email'] as { code?: string }[]).at(0)
      confirmationDialog.close()
      addErrorToast(
        !isNil(error) && 'code' in error
          ? t(`error_codes.${error.code as '101'}`)
          : t('errors.unknown')
      )
    }
  })

  const toggleEmailEdition = () => {
    setAllowEmailEdition((current) => !current)
  }

  return (
    <AccountSection title={t('models.account.fields.email.label', { capitalize: true })}>
      <SuperForm
        formRef={formRef}
        schema={SCHEMA}
        initialValues={{ email: me?.email }}
        onSubmit={async (values) => {
          await changeEmail({ newEmail: values.email })
        }}
      >
        <Flex $width='100%' $my='lg' $gap={'lg'} $alignItems='flex-start'>
          <EmailField
            name='email'
            label={''}
            disabled={!allowEmailEdition}
            // May not be necessary in the future since we should ne have unverified emails
            // And also since we chose not to show a waiting state while the email verification is pending
            rightAdornment={
              allowEmailEdition ? undefined : <VerifiedBadge isVerified={me?.isEmailVerified} />
            }
          />
          <Flex $mt={'md'} $gap='md' $alignSelf='flex-start'>
            {allowEmailEdition ? (
              <>
                <CancelButton
                  onPress={() => {
                    toggleEmailEdition()
                  }}
                />
                <ConfirmButton onPress={confirmationDialog.open} />
              </>
            ) : (
              <Button variant='compact' onPress={toggleEmailEdition}>
                {t('actions.modify')}
              </Button>
            )}
          </Flex>
        </Flex>
        <FormAwareConfirmationModal
          isOpen={confirmationDialog.isOpen}
          close={confirmationDialog.close}
          sendVerificationEmailRequest={changeEmailRequest}
        />
      </SuperForm>
    </AccountSection>
  )
}

export default ChangeEmail
